import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import CustomLoader from "../custom/CustomLoader";
import PaginationControl from "../custom/PaginationControl";
import { useFetch, useMutate, buildFilterPayload } from "../../hooks/queries";
import { toast } from "react-toastify";
import moment from "moment";
import { FaDownload } from "react-icons/fa";

const AssignmentsDataModal = ({
  show,
  onHide,
  modalType, // "assignmentsVsOutcomes", "submissions", "outcomesLinked"
  statusFilter, // For assignmentsVsOutcomes: "linked", "notLinked", "all"
  // For submissions: "graded", "notGraded", "all"
  // For outcomesLinked: "linked", "notLinked", "all"
  startDate,
  endDate,
  schoolId,
  operatorId,
}) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // Determine API endpoints and filters based on modalType and statusFilter
  const getApiConfig = () => {
    const baseFilters = {
      startDate,
      endDate,
      selectedSchool: { value: schoolId },
      selectedOperator: { value: operatorId },
    };

    switch (modalType) {
      case "assignmentsVsOutcomes":
        return {
          url:
            process.env.REACT_APP_API_URL +
            "Assignment/GetAssignmentsVsLearningOutcomes",
          exportUrl:
            process.env.REACT_APP_API_URL +
            "Assignment/ExportAssignmentsVsLearningOutcomes",
          title: "Assignments VS Learning Outcomes",
          filterKey: "HasLearningOutcomes",
          filterValue:
            statusFilter === "all" ? null : statusFilter === "linked",
        };
      case "submissions":
        return {
          url:
            process.env.REACT_APP_API_URL +
            "Assignment/GetAssignmentSubmissions",
          exportUrl:
            process.env.REACT_APP_API_URL +
            "Assignment/ExportAssignmentSubmissions",
          title: "Assignment Submissions",
          filterKey: "IsGraded",
          filterValue:
            statusFilter === "all" ? null : statusFilter === "graded",
        };
      case "outcomesLinked":
        return {
          url:
            process.env.REACT_APP_API_URL +
            "Assignment/GetLearningOutcomesLinkedToAssignments",
          exportUrl:
            process.env.REACT_APP_API_URL +
            "Assignment/ExportLearningOutcomesLinkedToAssignments",
          title: "Learning Outcomes Linked To Assignments",
          filterKey: "IsLinked",
          filterValue:
            statusFilter === "all" ? null : statusFilter === "linked",
        };
      default:
        return null;
    }
  };

  const apiConfig = getApiConfig();

  // Build request payload
  const buildRequestPayload = () => {
    const payload = {
      OperatorId: operatorId || 0,
      SchoolId: schoolId || 0,
      StartDate: startDate,
      EndDate: endDate,
      PageNumber: page,
      PageSize: perPage,
    };

    if (apiConfig?.filterKey) {
      payload[apiConfig.filterKey] = apiConfig.filterValue;
    }

    return payload;
  };

  const {
    data: responseData,
    isPending,
    isLoading,
    isFetching,
    refetch,
  } = useFetch({
    queryKey: [
      modalType,
      statusFilter,
      page,
      perPage,
      startDate,
      endDate,
      schoolId,
      operatorId,
    ],
    url: apiConfig?.url,
    method: "PATCH",
    data: buildRequestPayload(),
    enabled: show && !!apiConfig,
  });

  const exportMutation = useMutate({
    mutationKey: ["exportAssignments", modalType],
    url: apiConfig?.exportUrl,
    method: "PATCH",
    axiosConfig: {
      responseType: "arraybuffer",
      timeout: 120000,
    },
    onSuccess: (data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = `${modalTitle}.xlsx`;

      if (navigator.msSaveOrOpenBlob) {
        navigator.msSaveBlob(blob, fileName);
      } else {
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      toast.success("Export completed successfully");
    },
    onError: (error) => {
      toast.error("Failed to export data");
    },
  });

  const loading = isPending || isLoading || isFetching;
  const exporting = exportMutation.isPending || exportMutation.isLoading;

  const data = responseData?.data || [];
  const totalCount = responseData?.totalCount || 0;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleExport = () => {
    const exportPayload = {
      OperatorId: operatorId || 0,
      SchoolId: schoolId || 0,
      StartDate: startDate,
      EndDate: endDate,
    };

    if (apiConfig?.filterKey) {
      exportPayload[apiConfig.filterKey] = apiConfig.filterValue;
    }

    exportMutation.mutate(exportPayload);
  };

  const handleClose = () => {
    setPage(1);
    onHide();
  };

  useEffect(() => {
    if (show) {
      setPage(1);
      refetch();
    }
  }, [show, statusFilter, modalType]);

  // Define columns based on modal type
  const getColumns = () => {
    switch (modalType) {
      case "assignmentsVsOutcomes":
        return [
          {
            id: "assignmentId",
            name: "Assignment ID",
            selector: (row) => row.assignmentId,
            sortable: true,
            width: "120px",
          },
          {
            id: "courseName",
            name: "Course Name",
            selector: (row) => row.courseName || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "200px",
          },
          {
            id: "assignmentName",
            name: "Assignment Title",
            selector: (row) => row.assignmentTitle,
            sortable: true,
            wrap: true,
            minWidth: "250px",
          },
          // {
          //   id: "dueAt",
          //   name: "Due Date",
          //   selector: (row) =>
          //     row.dueAt ? moment(row.dueAt).format("YYYY-MM-DD HH:mm") : "N/A",
          //   sortable: true,
          //   width: "150px",
          // },
          {
            id: "rubricId",
            name: "Learning Outcome ID",
            selector: (row) => row.learningOutcomeId || "N/A",
            sortable: true,
            width: "150px",
            omit: statusFilter === "notLinked",
          },
          {
            id: "rubricDescription",
            name: "Learning Outcome Name",
            selector: (row) => row.learningOutcomeName || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "200px",
            omit: statusFilter === "notLinked",
          },
          // {
          //   id: "sectionId",
          //   name: "Section ID",
          //   selector: (row) => row.sectionId || "N/A",
          //   sortable: true,
          //   width: "120px",
          // },
          {
            id: "sectionName",
            name: "Section Name",
            selector: (row) => row.sectionName || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "150px",
          },
          {
            id: "learningOutcomeGroupId",
            name: "Learning Outcome Group ID",
            selector: (row) => row.learningOutcomeGroupId || "N/A",
            sortable: true,
            width: "180px",
          },
          {
            id: "learningOutcomeGroupTitle",
            name: "Learning Outcome Group Title",
            selector: (row) => row.learningOutcomeGroupTitle || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "200px",
          },
          {
            id: "hasLearningOutcomes",
            name: "Has Learning Outcomes",
            selector: (row) => (row.hasLearningOutcomes ? "Yes" : "No"),
            sortable: true,
            width: "150px",
          },
        ];

      case "submissions":
        return [
          {
            id: "submissionId",
            name: "Submission ID",
            selector: (row) => row.submissionId,
            sortable: true,
            width: "130px",
          },
          {
            id: "assignmentId",
            name: "Assignment ID",
            selector: (row) => row.assignmentId,
            sortable: true,
            width: "130px",
          },
          {
            id: "assignmentTitle",
            name: "Assignment Title",
            selector: (row) => row.assignmentTitle || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "200px",
          },
          {
            id: "userId",
            name: "User ID",
            selector: (row) => row.userId,
            sortable: true,
            width: "100px",
          },
          {
            id: "score",
            name: "Score",
            selector: (row) => (row.score !== null ? row.score : "N/A"),
            sortable: true,
            width: "100px",
          },
          {
            id: "submittedDate",
            name: "Submission Date",
            selector: (row) =>
              row.submissionDate
                ? moment(row.submissionDate).format("YYYY-MM-DD HH:mm")
                : "N/A",
            sortable: true,
            width: "150px",
          },
          {
            id: "isGraded",
            name: "Is Graded",
            selector: (row) => (row.isGraded ? "Yes" : "No"),
            sortable: true,
            width: "100px",
          },
          {
            id: "schoolName",
            name: "School Name",
            selector: (row) => row.schoolName || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "200px",
          },
          // {
          //   id: "sectionId",
          //   name: "Section ID",
          //   selector: (row) => row.sectionId || "N/A",
          //   sortable: true,
          //   width: "120px",
          // },
          {
            id: "sectionName",
            name: "Section Name",
            selector: (row) => row.sectionName || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "150px",
          },
        ];

      case "outcomesLinked":
        return [
          {
            id: "rubricId",
            name: "Learning Outcome ID",
            selector: (row) => row.learningOutcomeId || "N/A",
            sortable: true,
            width: "180px",
          },
          {
            id: "rubricDescription",
            name: "Learning Outcome Name",
            selector: (row) => row.learningOutcomeName || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "300px",
          },
          {
            id: "assignmentsCount",
            name: "Assignments Count",
            selector: (row) => row.assignmentsCount,
            sortable: true,
            width: "150px",
          },
          {
            id: "learningOutcomeGroupId",
            name: "Learning Outcome Group ID",
            selector: (row) => row.learningOutcomeGroupId || "N/A",
            sortable: true,
            width: "200px",
          },
          {
            id: "learningOutcomeGroupTitle",
            name: "Learning Outcome Group Title",
            selector: (row) => row.learningOutcomeGroupTitle || "N/A",
            sortable: true,
            wrap: true,
            minWidth: "250px",
          },
        ];

      default:
        return [];
    }
  };

  const getStatusLabel = () => {
    switch (modalType) {
      case "assignmentsVsOutcomes":
        if (statusFilter === "linked") return "Linked to Learning Outcomes";
        if (statusFilter === "notLinked")
          return "Not Linked to Learning Outcomes";
        return "All Assignments";
      case "submissions":
        if (statusFilter === "graded") return "Graded Submissions";
        if (statusFilter === "notGraded") return "Not Graded Submissions";
        return "All Submissions";
      case "outcomesLinked":
        if (statusFilter === "linked") return "Linked Learning Outcomes";
        if (statusFilter === "notLinked") return "Not Linked Learning Outcomes";
        return "All Learning Outcomes";
      default:
        return "";
    }
  };

  if (!apiConfig) return null;

  const modalTitle = `${apiConfig.title} - ${getStatusLabel()}`;

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header
        style={{
          backgroundColor: "#287a9f",
          color: "white",
          borderRadius: "0",
        }}
      >
        <Modal.Title style={{ fontSize: "18px", fontWeight: "bold" }}>
          {modalTitle}
        </Modal.Title>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={handleClose}
          aria-label="Close"
        ></button>
      </Modal.Header>
      <Modal.Body style={{ padding: "0" }}>
        <div style={{ padding: "15px", borderBottom: "1px solid #ddd" }}>
          <button
            className="btn cts-btn-primary-3 btn-sm"
            onClick={handleExport}
            disabled={exporting || loading || totalCount === 0}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <FaDownload />
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
        <DataTable
          columns={getColumns()}
          data={data}
          progressPending={loading}
          progressComponent={<CustomLoader />}
          fixedHeader
          fixedHeaderScrollHeight="50vh"
          defaultSortFieldId={1}
          noDataComponent={
            <div style={{ padding: "24px", textAlign: "center" }}>
              No records found
            </div>
          }
        />
        <PaginationControl
          totalCount={totalCount}
          currentPage={page}
          pageSize={perPage}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePerRowsChange}
          pageSizeOptions={[20, 50, 100, 200]}
          disabled={loading}
        />
      </Modal.Body>
    </Modal>
  );
};

export default AssignmentsDataModal;
